import Keycloak from 'keycloak-js';

// IMPORTANT: Keep top level roles in correct descending order (owner > admin > manager > ...)
const TOP_LEVEL_ROLES = ['owner', 'admin', 'manager'];
const READ_ONLY_ROLES = ['viewer', 'global_viewer'];
const NAVIGATION = [
  {
    title: 'games',
    icon: 'mdi-apps',
    link: '/games',
    mobile: true,
    roles: ['owner', 'admin', 'manager', 'viewer', 'global_viewer'],
  }, {
    title: 'reports',
    icon: 'mdi-note-text',
    link: '/reports',
    mobile: true,
    roles: ['owner', 'manager', 'viewer', 'global_viewer'],
  }, {
    title: 'companies',
    icon: 'mdi-account-multiple',
    link: '/companies',
    mobile: true,
    roles: ['owner', 'admin'],
  }, {
    title: 'players',
    icon: 'mdi-account',
    link: '/players',
    mobile: true,
    roles: ['owner', 'admin', 'manager'],
  }, {
    title: 'promotions',
    icon: 'mdi-tag',
    link: '/promotions',
    mobile: true,
    roles: ['owner', 'admin', 'manager'],
  }, {
    title: 'developers',
    icon: 'mdi-wrench',
    link: '/developers',
    mobile: false,
    roles: ['owner', 'admin', 'developer'],
  }, {
    title: 'assets',
    icon: 'mdi-briefcase-variant',
    link: '/assets',
    mobile: false,
    roles: ['owner', 'admin', 'manager', 'developer'],
  }, {
    title: 'boost',
    icon: 'mdi-rocket-launch',
    link: '/boost',
    mobile: true,
    roles: ['owner', 'boost_admin'],
  },
];

class KeycloakService {
  constructor() {
    this.userData = null;
    this.client = new Keycloak({
      url: process.env.VUE_APP_KEYCLOAK_AUTHORIZATION_SERVER_URL,
      realm: process.env.VUE_APP_KEYCLOAK_REALM,
      clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
    });
  }

  get isAuthenticated() {
    return this.client.authenticated;
  }

  get authToken() {
    return this.client.token;
  }

  get authorizedNavigation() {
    return NAVIGATION.filter((item) => item.roles.some((role) => this.hasRole(role)));
  }

  // Top level roles are roles that inherit other roles, for example "owner"
  // role inherits "admin" and "admin" inherits "manager" role.
  // User can have multiple roles, for example a single user can be "admin" and "boost admin".
  // Read only users are users with low level rights (some clients etc.)
  get userRole() {
    const userRoles = this.client.resourceAccess[this.client.clientId]?.roles || [];
    const topLevelRole = TOP_LEVEL_ROLES.find((role) => this.hasRole(role));
    const isReadOnlyRole = READ_ONLY_ROLES.some((role) => this.hasRole(role));

    return {
      isOwner: topLevelRole === 'owner', // inherits: [user, viewer, developer, manager, boost_admin, admin]
      isAdmin: topLevelRole === 'admin', // inherits: [user, viewer, developer, manager]
      isManager: topLevelRole === 'manager', // inherits: [user, viewer, developer]
      isDeveloper: !topLevelRole && this.hasRole('developer'), // inherits: [user]
      isGlobalViewerUser: !topLevelRole && this.hasRole('global_viewer'), // inherits: [user]
      isViewer: !topLevelRole && this.hasRole('viewer'), // inherits: [user]
      isBoostAdmin: this.hasRole('boost_admin'), // can be combined with other roles
      isBoostOnlyUser: userRoles.length === 1 && this.hasRole('boost_admin'), // User with access only for boost
      isReadOnlyUser: !topLevelRole && isReadOnlyRole,
    };
  }

  async init() {
    const isAuthenticated = await this.client.init({
      responseType: 'code',
      flow: 'standard',
      enablePkce: true,
      pkceMethod: 'S256',
      onLoad: 'login-required',
    });

    if (isAuthenticated) {
      this.setUserData();
    }

    return isAuthenticated;
  }

  setUserData() {
    const tokenParsed = this.client.tokenParsed || {};
    const firstNameInitial = tokenParsed.given_name?.[0]?.toUpperCase() || 'N';
    const lastNameInitial = tokenParsed.family_name?.[0]?.toUpperCase() || 'N';

    this.userData = {
      id: tokenParsed.sub,
      email: tokenParsed.email,
      firstName: tokenParsed.given_name,
      lastName: tokenParsed.family_name,
      fullName: tokenParsed.name,
      initials: `${firstNameInitial}${lastNameInitial}`,
    };
  }

  hasRole(role) {
    return this.client.hasResourceRole(role);
  }

  logout() {
    this.client.logout();
  }

  refreshToken() {
    const tokenExpirationTime = this.client.tokenParsed.exp + this.client.timeSkew;
    const currentTime = new Date().getTime() / 1000;
    const lifespan = Math.round(tokenExpirationTime - currentTime) * 1000;

    setInterval(() => {
      this.client.updateToken(lifespan).catch(() => {
        this.logout();
      });
    }, lifespan);
  }
}

// eslint-disable-next-line import/prefer-default-export
export const keycloakService = new KeycloakService();
